import Box from '@mui/material/Box';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSuspenseQuery, useQueryErrorResetBoundary } from '@tanstack/react-query';
import { trackError } from '@proposify/frontend-observability';
import type { AssignedCustomFieldsQueryOptions } from 'services/custom-fields/customFieldsQueries';
import { ReadonlyCustomFieldInput } from './custom-field-input/ReadonlyCustomFieldInput';
import { EditableCustomFieldInput } from './custom-field-input/EditableCustomFieldInput';

type AssignedCustomFieldsListProps = {
  canEdit: boolean;
  query: AssignedCustomFieldsQueryOptions;
};

export function AssignedCustomFieldsListLoader({ canEdit, query }: AssignedCustomFieldsListProps) {
  const { data: assignedCustomFields } = useSuspenseQuery(query);

  if (assignedCustomFields.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {assignedCustomFields.map((field) => {
        return canEdit ? (
          <EditableCustomFieldInput key={field.id} field={field} />
        ) : (
          <ReadonlyCustomFieldInput key={field.id} field={field} />
        );
      })}
    </Box>
  );
}

export function AssignedCustomFieldsList({ canEdit, query }: AssignedCustomFieldsListProps) {
  const { reset } = useQueryErrorResetBoundary();

  return (
    <ErrorBoundary
      onReset={reset}
      fallbackRender={() => null}
      onError={(error, info) => {
        trackError(new Error('AssignedCustomFieldsList rendering failed', { cause: error }), {
          component: 'AssignedCustomFieldsList',
          errorFrom: 'editor-remote',
          reactErrorInfo: info,
        });
      }}
    >
      <Suspense fallback={null}>
        <AssignedCustomFieldsListLoader canEdit={canEdit} query={query} />
      </Suspense>
    </ErrorBoundary>
  );
}
